import { NavLink, Outlet } from 'react-router-dom';
import { useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '../shared/auth';
import { Icon, type IconName } from '../ui/Icon';
import { ChangeOwnPasswordModal } from '../features/users/ChangeOwnPasswordModal';

interface NavLinkDef {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

interface NavSection {
  title?: string;
  items: NavLinkDef[];
}

// Mobile bottom tab — the 4 most-used. Rest live in the More drawer.
const MOBILE_TABS: NavLinkDef[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/attendance', label: 'Attendance', icon: 'check-square' },
  { to: '/schedule', label: 'Schedule', icon: 'calendar' },
  { to: '/intake', label: 'Intake', icon: 'user-plus' },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const isAdmin = user?.role === 'ADMIN';

  const NAV: NavSection[] = [
    {
      items: [
        { to: '/', label: 'Dashboard', icon: 'home', end: true },
        { to: '/attendance', label: 'Attendance', icon: 'check-square' },
        { to: '/schedule', label: 'Schedule', icon: 'calendar' },
        { to: '/pipeline', label: 'Pipeline', icon: 'layers' },
        { to: '/intake', label: 'Intake', icon: 'user-plus' },
      ],
    },
    {
      title: 'Records',
      items: [
        { to: '/clients', label: 'Census Clients', icon: 'users' },
        { to: '/patients', label: 'EMR Clients', icon: 'users' },
        { to: '/providers', label: 'Providers', icon: 'user' },
        { to: '/groups', label: 'Groups', icon: 'layers' },
        { to: '/rooms', label: 'Rooms', icon: 'building' },
        { to: '/services', label: 'Service catalog', icon: 'list' },
        { to: '/master-data', label: 'Master library', icon: 'list' },
        { to: '/housing', label: 'Housing Clients', icon: 'building' },
        { to: '/locations', label: 'Locations', icon: 'building' },
      ],
    },
    {
      title: 'Insights',
      items: [
        { to: '/reports', label: 'Reports', icon: 'bar-chart' },
        { to: '/scheduled-reports', label: 'Scheduled reports', icon: 'bell' },
        { to: '/notifications', label: 'Notifications', icon: 'bell' },
        ...(isAdmin
          ? ([
              { to: '/users', label: 'Users', icon: 'user' },
              { to: '/settings', label: 'Settings', icon: 'settings' },
            ] as NavLinkDef[])
          : []),
      ],
    },
  ];

  return (
    <div className="flex h-full bg-surface-subtle">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-white md:flex">
        <div className="flex items-center gap-2 border-b border-line-subtle px-5 py-5">
          <div className="grid h-8 w-8 place-items-center rounded-md bg-brand-600 text-white">
            <Icon name="calendar" size={18} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-ink">Census</div>
            <div className="text-2xs text-ink-subtle">Scheduler</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
          {NAV.map((section, i) => (
            <div key={i}>
              {section.title && <div className="nav-section">{section.title}</div>}
              {section.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    clsx('nav-item', isActive && 'nav-item-active')
                  }
                >
                  <Icon name={n.icon} size={16} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-line-subtle px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-full bg-brand-100 text-2xs font-semibold text-brand-700">
              {(user?.fullName ?? 'U').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 text-2xs leading-tight">
              <div className="truncate font-medium text-ink">{user?.fullName}</div>
              <div className="truncate text-ink-subtle">{user?.email}</div>
            </div>
            <button
              onClick={() => setChangePasswordOpen(true)}
              className="grid h-8 w-8 place-items-center rounded-md text-ink-subtle hover:bg-surface-muted hover:text-ink"
              title="Change my password"
            >
              <Icon name="key" size={16} />
            </button>
            <button
              onClick={logout}
              className="grid h-8 w-8 place-items-center rounded-md text-ink-subtle hover:bg-surface-muted hover:text-ink"
              title="Sign out"
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="app-header flex items-center justify-between gap-3 border-b border-line bg-white px-4 py-3 md:hidden">
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-md bg-brand-600 text-white">
              <Icon name="calendar" size={14} />
            </div>
            <span className="text-sm font-semibold text-ink">Census</span>
          </div>
          <button
            onClick={() => setDrawerOpen(true)}
            className="grid h-9 w-9 place-items-center rounded-md text-ink-muted hover:bg-surface-muted"
            aria-label="Open menu"
          >
            <Icon name="menu" size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4 pb-24 md:p-6 md:pb-6">
          <Outlet />
        </div>

        {/* Mobile bottom tab bar */}
        <nav className="no-print fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t border-line bg-white shadow-lg md:hidden">
          {MOBILE_TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                clsx(
                  'flex flex-col items-center gap-0.5 py-2.5 text-2xs font-medium transition',
                  isActive ? 'text-brand-700' : 'text-ink-subtle hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon name={t.icon} size={22} />
                  <span>{t.label}</span>
                  <span
                    className={clsx(
                      'mt-0.5 h-0.5 w-6 rounded-full',
                      isActive ? 'bg-brand-600' : 'bg-transparent',
                    )}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </main>

      {/* Mobile drawer with full nav */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink/40 md:hidden"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-72 max-w-[85vw] overflow-y-auto bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-line px-4 py-4">
              <span className="text-sm font-semibold">Menu</span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-md hover:bg-surface-muted"
              >
                <Icon name="x" size={18} />
              </button>
            </div>
            <nav className="space-y-0.5 px-2 py-3">
              {NAV.map((section, i) => (
                <div key={i}>
                  {section.title && <div className="nav-section">{section.title}</div>}
                  {section.items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      onClick={() => setDrawerOpen(false)}
                      className={({ isActive }) =>
                        clsx('nav-item', isActive && 'nav-item-active')
                      }
                    >
                      <Icon name={n.icon} size={16} />
                      <span>{n.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="border-t border-line-subtle px-4 py-3">
              <div className="text-2xs text-ink-subtle">{user?.fullName}</div>
              <div className="mb-2 text-2xs text-ink-subtle">{user?.email}</div>
              <button
                onClick={() => {
                  setDrawerOpen(false);
                  setChangePasswordOpen(true);
                }}
                className="btn-secondary mb-2 w-full"
              >
                <Icon name="key" size={14} /> Change password
              </button>
              <button onClick={logout} className="btn-secondary w-full">
                <Icon name="logout" size={14} /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Self-service password change. Available from desktop sidebar
          (key icon) and mobile drawer (button). Calls
          POST /users/me/change-password with current + new password. */}
      {changePasswordOpen && (
        <ChangeOwnPasswordModal onClose={() => setChangePasswordOpen(false)} />
      )}
    </div>
  );
}
